import { type FC, type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { confirmPasswordReset, requestPasswordReset, resetErrorMessage as apiErrorMessage } from '../api/password-reset.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';

const Shell: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-slate-100">
    <div className="w-full max-w-sm space-y-5">
      <h1 className="text-center text-xl font-bold">{title}</h1>
      {children}
      <p className="text-center text-xs text-slate-500">
        <Link to="/login" className="text-blue-400 hover:text-blue-300">Back to sign in</Link>
      </p>
    </div>
  </div>
);

/** Public page: ask for a reset link. The answer never says whether the email has an account. */
export const ForgotPassword: FC = () => {
  const [email, setEmail] = useState('');
  const request = useMutation({ mutationFn: () => requestPasswordReset(email.trim()) });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    request.mutate();
  };

  return (
    <Shell title="Reset your password">
      {request.isSuccess && request.data.emailEnabled === false ? (
        <p className="rounded-lg border border-amber-900/50 bg-amber-900/10 px-3 py-2 text-center text-sm text-amber-400">
          {request.data.message}
        </p>
      ) : request.isSuccess ? (
        <p className="rounded-lg border border-emerald-900/50 bg-emerald-900/10 px-3 py-2 text-center text-sm text-emerald-400">
          If an account exists for {email.trim()}, a link to reset the password is on its way. It expires in 60 minutes.
          If nothing arrives, check spam, or ask your agency.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-center text-sm text-slate-400">Enter the email you sign in with and we&apos;ll send you a link.</p>
          <Input type="email" required maxLength={255} placeholder="you@example.com" value={email}
            onChange={(e) => setEmail(e.target.value)} aria-label="Email" autoComplete="email" />
          {request.isError && (
            <p className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-xs text-red-400">
              {apiErrorMessage(request.error, 'Could not send the request. Try again in a few minutes.')}
            </p>
          )}
          <Button type="submit" className="w-full" loading={request.isPending}>Send reset link</Button>
        </form>
      )}
    </Shell>
  );
};

/** The token travels in the URL fragment, which browsers never send to servers. */
function readToken(): string {
  try {
    return decodeURIComponent(window.location.hash.replace(/^#/, '').trim());
  } catch {
    return '';
  }
}

/** Public page reached from the emailed link: choose a new password. */
export const ResetPassword: FC = () => {
  const [token] = useState(readToken);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const reset = useMutation({ mutationFn: () => confirmPasswordReset(token, password) });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    reset.mutate();
  };

  return (
    <Shell title="Choose a new password">
      {reset.isSuccess ? (
        <div className="space-y-3 text-center">
          <p className="text-sm text-emerald-400">Your password was changed. Every other session has been signed out.</p>
          <Link to="/login" className="text-sm text-blue-400 hover:text-blue-300">Sign in with your new password</Link>
        </div>
      ) : token === '' ? (
        <p className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-center text-sm text-red-400">
          This reset link is incomplete. Open the link from the email again, or <Link to="/forgot-password" className="underline">ask for a new one</Link>.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input type="password" required minLength={8} maxLength={128} placeholder="New password (min 8 characters)"
            value={password} onChange={(e) => setPassword(e.target.value)} aria-label="New password" autoComplete="new-password" />
          <Input type="password" required minLength={8} maxLength={128} placeholder="Confirm new password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-label="Confirm new password" autoComplete="new-password" />
          {(mismatch || reset.isError) && (
            <p className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-xs text-red-400">
              {mismatch ? 'Passwords do not match' : apiErrorMessage(reset.error, 'Could not change the password')}
              {!mismatch && <> <Link to="/forgot-password" className="underline">Ask for a new link</Link>.</>}
            </p>
          )}
          <Button type="submit" className="w-full" loading={reset.isPending}>Change password</Button>
        </form>
      )}
    </Shell>
  );
};
